import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { getQuote } from "../api/quote/getQuote";
import { getQuoteDealerPricingRequestCounts } from "../api/quote/getQuoteDealerPricingRequestCounts";
import { getQuoteOptions } from "../api/quote/getQuoteOptions";
import { getQuoteVehicleImages } from "../api/quote/getQuoteVehicleImages";
import { getQuoteVehicleInfo } from "../api/quote/getQuoteVehicleInfo";
import { updateQuote } from "../api/quote/updateQuote";
import { formatQuote } from "../helpers/format";
import {
  PersonaliseQuoteInputs,
  QuotePersonalisationScope,
} from "../helpers/quotes";
import { useAuth } from "../providers/AuthProvider";
import {
  buildQuoteOptionsPayload,
  prefillQuoteOptionsForm,
} from "../quote/helpers/quoteOptions";
import { ErrorCode } from "../types/Error";

type UseQuotePersonalisationParams = {
  quoteId: string;
  scope?: QuotePersonalisationScope;
};

export const useQuotePersonalisation = ({
  quoteId,
  scope = "all",
}: UseQuotePersonalisationParams) => {
  const { getScopedPageUrl } = useAuth();
  const { control, handleSubmit, setValue, getValues } =
    useForm<PersonaliseQuoteInputs>({
      mode: "onTouched",
    });
  const [formPrefilledFor, setFormPrefilledFor] = useState<{
    quoteId: string;
    dataUpdatedAt: number;
  } | null>(null);

  const quote = getQuote(quoteId, {
    mutationOptions: {
      errorNavPaths: { [ErrorCode.QuoteDeleted]: getScopedPageUrl("home") },
    },
  });
  const quoteVehicleImages = getQuoteVehicleImages(quoteId);
  const quoteVehicleInfo = getQuoteVehicleInfo(quoteId);
  const pricingRequestCounts = getQuoteDealerPricingRequestCounts(quoteId);
  const vehicleOptions = getQuoteOptions(quoteId);
  const { mutateAsync, isLoading: isSubmitting } = updateQuote(quoteId);

  if (quote.isError) {
    throw quote.error;
  }

  if (quoteVehicleInfo.isError) {
    throw quoteVehicleInfo.error;
  }

  if (vehicleOptions.isError) {
    throw vehicleOptions.error;
  }

  if (pricingRequestCounts.isError) {
    throw pricingRequestCounts.error;
  }

  useEffect(() => {
    if (quoteVehicleInfo.isSuccess) {
      prefillQuoteOptionsForm(setValue, getValues, quoteVehicleInfo.data);
      setFormPrefilledFor({
        quoteId,
        dataUpdatedAt: quoteVehicleInfo.dataUpdatedAt,
      });
    }
  }, [quoteId, quoteVehicleInfo.isFetching]);

  const isFormReady =
    quoteVehicleInfo.isSuccess &&
    formPrefilledFor?.quoteId === quoteId &&
    formPrefilledFor.dataUpdatedAt === quoteVehicleInfo.dataUpdatedAt;

  const isLoading =
    vehicleOptions.isFetching ||
    quoteVehicleInfo.isFetching ||
    quote.isFetching ||
    pricingRequestCounts.isFetching ||
    (quoteVehicleInfo.isSuccess && !isFormReady);
  const isSuccess =
    vehicleOptions.isSuccess &&
    quoteVehicleInfo.isSuccess &&
    quote.isSuccess &&
    pricingRequestCounts.isSuccess &&
    isFormReady;

  const hasPricingRequests =
    pricingRequestCounts.isSuccess && pricingRequestCounts.data.total > 0;

  const submitOptions = async (formValues: PersonaliseQuoteInputs) => {
    if (!quoteVehicleInfo.isSuccess || !vehicleOptions.isSuccess) {
      return false;
    }

    const payload = buildQuoteOptionsPayload(
      formValues,
      quoteVehicleInfo.data,
      vehicleOptions.data,
      scope
    );

    if (!payload) {
      return false;
    }

    await mutateAsync(payload);
    return true;
  };

  return {
    control,
    handleSubmit,
    quote,
    quoteVehicleImages,
    quoteVehicleInfo,
    vehicleOptions,
    pricingRequestCounts,
    isLoading,
    isSuccess,
    isFormReady,
    isSubmitting,
    hasPricingRequests,
    submitOptions,
    formattedQuote: quote.isSuccess ? formatQuote(quote.data) : undefined,
  };
};
